import { API_URL } from '../config/api';
import { authenticatedFetch, publicFetch } from '../utils/authenticatedFetch';

// Types for our responses
export interface StupaLocation {
    id: string;
    coordinates: [number, number];
    type: 'active' | 'planned';
    name: string;
    waterSaved?: number;
    initialCost?: number;
    image?: string;
    estimatedWaterSavings?: number;
    donationGoal?: number;
    currentDonations?: number;
}

export interface StupaData {
    id: number;
    name: string;
    flow_rate: number;
    temperature: number;
    last_updated: string;
    image_urls: string[];
}

export interface StupaMonitoringData {
    telemetry: {
        flowRate: number;
        temperature: number;
        humidity: number;
        pressure: number;
        lastUpdate: string;
    };
    liveImageUrl: string;
    coordinates: [number, number];
    name: string;
    id: string;
    waterSaved: number;
    initialCost: number;
    type: string;
}

// API calls
export const stupaService = {
    // Get all stupa locations
    getStupaLocations: async (): Promise<StupaLocation[]> => {
        try {
            const response = await publicFetch(`${API_URL}/public/stupas/locations`);
            if (!response.ok) {
                throw new Error(`Unable to load glacier locations (${response.status})`);
            }
            const data = await response.json();
            const locations = Array.isArray(data.locations) ? data.locations : [];
            return locations.flatMap((loc: any) => {
                const longitude = Number(loc.lng ?? loc.longitude ?? loc.lon);
                const latitude = Number(loc.lat ?? loc.latitude);
                if (
                    !Number.isFinite(longitude)
                    || !Number.isFinite(latitude)
                    || longitude < -180
                    || longitude > 180
                    || latitude < -90
                    || latitude > 90
                ) {
                    return [];
                }
                return [{
                    id: String(loc.id ?? loc.icestupa_id ?? loc.name),
                    coordinates: [longitude, latitude] as [number, number],
                    type: 'active' as const,
                    name: String(loc.display_name ?? loc.name ?? loc.id ?? 'Glacier'),
                }];
            });
        } catch (error) {
            console.error('Error fetching stupa locations:', error);
            throw error;
        }
    },

    // Get specific stupa data
    getStupaData: async (stupaId: string): Promise<StupaData> => {
        try {
            const response = await authenticatedFetch(`${API_URL}/stupa/${stupaId}`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return await response.json();
        } catch (error) {
            console.error(`Error fetching stupa data for ID ${stupaId}:`, error);
            throw error;
        }
    }
};